import { useEffect, useState, type JSX } from 'react';
import type { LegalDocument } from '@procuai/contracts';
import { DialMark, Markdown, downloadMarkdown } from '@procuai/ui';
import { ApiError, getPublicLegalDocument } from '../api.js';

/**
 * Public legal pages (BP-22 OQ-1, resolved in-slice): /legal/privacy and /legal/subprocessors — the
 * two URLs the Customer Terms reference (clause 6.4 and DPA clause 8). No login, no shell, no org
 * context: the pages render the CURRENT version of the Privacy Policy / Sub-processor list from the
 * versioned store via the public, rate-limited endpoint. Contractual documents are NOT served here.
 */
export function LegalPage({ docType }: { docType: 'privacy_policy' | 'subprocessor_list' }): JSX.Element {
  const [doc, setDoc] = useState<LegalDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setDoc(null);
    setError(null);
    getPublicLegalDocument(docType)
      .then((d) => { if (live) setDoc(d); })
      .catch((e: unknown) => { if (live) setError(e instanceof ApiError ? e.message : 'This document is unavailable right now.'); });
    return () => { live = false; };
  }, [docType]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)', padding: '34px 20px 60px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: 'var(--ink)' }}>
          <DialMark size={20} /> procu<span style={{ color: 'var(--pine)' }}>ai</span>
        </div>
        <div style={{ background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 20px', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 600, letterSpacing: '-.01em' }}>{doc?.title ?? (error ? 'Document' : 'Loading…')}</h1>
              {doc && (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--ink-soft)', marginTop: 5, textTransform: 'uppercase' }}>
                  Version {doc.version}{doc.effectiveDate ? ` · effective ${doc.effectiveDate}` : ''}
                </div>
              )}
            </div>
            {doc && (
              <button
                onClick={() => downloadMarkdown(`procuai-${doc.docType}-${doc.version}.md`, doc.bodyMd)}
                style={{ border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--ink)', borderRadius: 'var(--radius-btn)', padding: '7px 13px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}
              >
                Download
              </button>
            )}
          </div>
          <div style={{ padding: '18px 22px' }}>
            {error && <div style={{ background: 'var(--red-tint)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', fontSize: 12.5 }}>{error}</div>}
            {doc && <Markdown body={doc.bodyMd} />}
            {!doc && !error && <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>Loading the document…</div>}
          </div>
        </div>
        <div style={{ textAlign: 'center', color: 'var(--ink-soft)', fontSize: 12, marginTop: 18 }}>
          Questions? Contact privacy@procuai.com
        </div>
      </div>
    </div>
  );
}
